/**
 * Pointer input: pan, wheel and pinch zoom, click to select, hover.
 *
 * Pointer Events rather than mouse plus touch, so a mouse, a trackpad, a pen
 * and a finger all arrive through one path and a two-finger pinch is just two
 * simultaneous pointers.
 *
 * Two details worth stating:
 *
 * * A drag that never moves more than a few pixels is a click, not a pan.
 *   Without that slop a click on a trackpad frequently registers as a
 *   one-pixel drag and the selection never fires.
 * * A trackpad pinch arrives as a `wheel` event with `ctrlKey` set — that is
 *   how every browser reports it — so it needs a different sensitivity from a
 *   real scroll wheel or the map leaps.
 */

export interface PointerHandlers {
  /** Drag delta in CSS pixels. */
  onPan(dx: number, dy: number): void;
  onPanStart(): void;
  onPanEnd(): void;
  /** Multiply the zoom about a canvas-relative CSS-pixel anchor. */
  onZoom(factor: number, anchor: readonly [number, number]): void;
  onHover(x: number, y: number): void;
  onHoverEnd(): void;
  onClick(x: number, y: number): void;
}

/** Movement in CSS pixels below which a press-and-release still counts as a click. */
const CLICK_SLOP = 4;

/** Wheel notches are ~100 deltaY; this maps one notch to a 1.1x zoom. */
const WHEEL_SENSITIVITY = 0.001;

/** A trackpad pinch reports much smaller deltas, so it needs a larger factor. */
const PINCH_SENSITIVITY = 0.01;

export class PointerController {
  private readonly canvas: HTMLCanvasElement;

  private readonly handlers: PointerHandlers;

  private readonly active = new Map<number, { x: number; y: number }>();

  private panning = false;

  private movedBy = 0;

  private lastX = 0;

  private lastY = 0;

  private pinchDistance = 0;

  private disposed = false;

  constructor(canvas: HTMLCanvasElement, handlers: PointerHandlers) {
    this.canvas = canvas;
    this.handlers = handlers;
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
    canvas.addEventListener('pointerleave', this.onPointerLeave);
    // Not passive: the whole point is to stop the page scrolling under a map
    // the user is zooming.
    canvas.addEventListener('wheel', this.onWheel, { passive: false });
    canvas.addEventListener('contextmenu', this.onContextMenu);
  }

  private local(event: PointerEvent | WheelEvent): [number, number] {
    const rect = this.canvas.getBoundingClientRect();
    return [event.clientX - rect.left, event.clientY - rect.top];
  }

  private onPointerDown = (event: PointerEvent): void => {
    if (this.disposed) return;
    const [x, y] = this.local(event);
    this.active.set(event.pointerId, { x, y });
    this.canvas.setPointerCapture(event.pointerId);
    if (this.active.size === 1) {
      this.panning = true;
      this.movedBy = 0;
      this.lastX = x;
      this.lastY = y;
      this.handlers.onPanStart();
    } else if (this.active.size === 2) {
      this.pinchDistance = this.currentPinchDistance();
    }
  };

  private currentPinchDistance(): number {
    const points = Array.from(this.active.values());
    const a = points[0];
    const b = points[1];
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  private pinchCentre(): [number, number] {
    const points = Array.from(this.active.values());
    const a = points[0];
    const b = points[1];
    if (!a || !b) return [0, 0];
    return [(a.x + b.x) / 2, (a.y + b.y) / 2];
  }

  private onPointerMove = (event: PointerEvent): void => {
    if (this.disposed) return;
    const [x, y] = this.local(event);
    const tracked = this.active.get(event.pointerId);
    if (tracked) {
      tracked.x = x;
      tracked.y = y;
    }

    if (this.active.size >= 2) {
      const distance = this.currentPinchDistance();
      if (this.pinchDistance > 0 && distance > 0) {
        this.handlers.onZoom(distance / this.pinchDistance, this.pinchCentre());
      }
      this.pinchDistance = distance;
      return;
    }

    if (this.panning && tracked) {
      const dx = x - this.lastX;
      const dy = y - this.lastY;
      this.lastX = x;
      this.lastY = y;
      this.movedBy += Math.abs(dx) + Math.abs(dy);
      if (this.movedBy > CLICK_SLOP) this.handlers.onPan(dx, dy);
      return;
    }

    this.handlers.onHover(x, y);
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (this.disposed) return;
    const [x, y] = this.local(event);
    const wasPanning = this.panning && this.active.size === 1;
    this.active.delete(event.pointerId);
    if (this.canvas.hasPointerCapture(event.pointerId)) {
      this.canvas.releasePointerCapture(event.pointerId);
    }
    if (this.active.size < 2) this.pinchDistance = 0;
    if (this.active.size === 0) {
      this.panning = false;
      this.handlers.onPanEnd();
      if (wasPanning && this.movedBy <= CLICK_SLOP) this.handlers.onClick(x, y);
    }
  };

  private onPointerLeave = (): void => {
    if (this.disposed) return;
    this.handlers.onHoverEnd();
  };

  private onWheel = (event: WheelEvent): void => {
    if (this.disposed) return;
    event.preventDefault();
    // deltaMode 1 is lines and 2 is pages; normalise both to something close to
    // the pixel deltas everything else reports.
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 100 : 1;
    const delta = event.deltaY * unit;
    const sensitivity = event.ctrlKey ? PINCH_SENSITIVITY : WHEEL_SENSITIVITY;
    const factor = Math.exp(-delta * sensitivity);
    this.handlers.onZoom(factor, this.local(event));
  };

  private onContextMenu = (event: Event): void => {
    // A right-click on the map should not open the browser menu mid-drag.
    if (this.active.size > 0) event.preventDefault();
  };

  destroy(): void {
    if (this.disposed) return;
    this.disposed = true;
    const canvas = this.canvas;
    canvas.removeEventListener('pointerdown', this.onPointerDown);
    canvas.removeEventListener('pointermove', this.onPointerMove);
    canvas.removeEventListener('pointerup', this.onPointerUp);
    canvas.removeEventListener('pointercancel', this.onPointerUp);
    canvas.removeEventListener('pointerleave', this.onPointerLeave);
    canvas.removeEventListener('wheel', this.onWheel);
    canvas.removeEventListener('contextmenu', this.onContextMenu);
    this.active.clear();
  }
}
